import type { Messages } from "@prop/i18n";
import type en from "../en/trade";

export default {
  nav: { accounts: "Cuentas", terminal: "Terminal", programs: "Programas", faq: "Preguntas", settings: "Ajustes", signIn: "Iniciar sesión" },
  markets: { crypto: "Cripto", fx: "FX / CFD", futures: "Futuros" },
  common: { replay: "REPLAY", noData: "Sin datos", loading: "Cargando…", close: "Cerrar", more: "Más", less: "Menos", none: "—", back: "Atrás" },
  status: { LIVE: "En vivo", degraded: "Degradado", closed: "Mercado cerrado", connecting: "Conectando…", offline: "Sin conexión — reconectando" },
  terminal: {
    title: "Terminal",
    account: "Cuenta",
    instrument: "Instrumento",
    search: "Buscar instrumentos",
    searchHint: "Pulsa / para buscar",
    noAccount: "Abre una cuenta primero para operar.",
    toAccounts: "Ir a cuentas",
    layout: "Diseño",
    layouts: { standard: "Estándar", chart: "Enfoque en gráfico", dom: "DOM / escalera" },
    book: "Libro de órdenes",
    tape: "Operaciones recientes",
    ladder: "DOM",
    step: "Paso",
    price: "Precio",
    size: "Tamaño",
    time: "Hora",
    bid: "Bid",
    ask: "Ask",
    spread: "Spread",
    mark: "Mark",
    last: "Último",
    hotkeys: "Atajos",
    hk: { buy: "Comprar a mercado", sell: "Vender a mercado", cancel: "Cancelar todas las órdenes", flatten: "Cerrar posiciones", search: "Buscar", sheet: "Teclas y gestos", close: "Cerrar un panel" },
    ladderHint: "Haz clic en una celda bid para comprar a ese precio, en una ask para vender; tus órdenes se muestran junto al precio.",
    phone: {
      amount: "Monto, USD",
      markets: "Mercados",
      all: "Todos",
      recent: "Recientes",
      favorites: "Favoritos",
      star: "Añadir a favoritos",
      unstar: "Quitar de favoritos",
      stats: "Datos del mercado",
      account: "Cuenta",
      closeAll: "Cerrar todo",
      noResults: "No se encontró nada. Prueba un ticker: BTC, EUR, GOLD.",
    },
    cme: "Los datos de mercado de CME requieren antes el acuerdo de suscriptor.",
    signCme: "Revisar y firmar",
    groups: { crypto: "Perpetuos cripto", currency: "Divisas", metal: "Metales", energy: "Energía", index: "Índices", stock: "Acciones", futures: "Futuros" },
    kinds: { perp: "Perp", fx: "FX", cfd: "CFD", future: "Futuro" },
    accountSize: "Tamaño {v}",
    accountEquity: "equity {v}",
    allAccounts: "Todas las cuentas",
    feed: {
      label: {
        live: "En vivo",
        connecting: "Conectando…",
        offline: "Sin conexión",
        waiting: "Esperando datos",
        syncing: "Sincronizando",
        stale: "Con retraso",
        closed: "Mercado cerrado",
        cme: "Falta el acuerdo",
        licence: "Sin licencia de datos",
        noSource: "Sin datos",
        denied: "Sin acceso",
      },
      why: {
        live: "Los precios y el libro de órdenes llegan en tiempo real.",
        connecting: "Conectando al flujo del mercado.",
        offline: "Se cortó la conexión con el flujo del mercado.",
        waiting: "El feed de este mercado aún no ha enviado nada.",
        syncing: "El libro de órdenes se está reconstruyendo desde el exchange — suele tardar unos segundos.",
        stale: "El exchange no ha actualizado el libro en unos segundos, así que los precios pueden ir con retraso.",
        closed: "La sesión del exchange está cerrada; los precios vuelven en la apertura.",
        cme: "CME pide a cada usuario aceptar su acuerdo de suscriptor antes de mostrar datos de futuros.",
        licence: "Los datos en vivo de futuros CME requieren una licencia de datos del exchange, que este entorno aún no tiene.",
        noSource: "No hay ninguna fuente de datos conectada para este mercado ahora mismo.",
        denied: "Esta cuenta no puede recibir datos de este mercado.",
      },
      todo: {
        offline: "Se reconecta solo; tus órdenes se quedan como están.",
        stale: "Se resincroniza solo; opera con cuidado hasta que el indicador diga En vivo.",
        closed: "Elige un instrumento cuyo mercado esté abierto.",
        cme: "Revisa y firma el acuerdo encima del gráfico.",
        licence: "Mientras tanto, opera cripto o FX: cambia de cuenta arriba a la izquierda.",
        noSource: "Elige otro instrumento o vuelve más tarde.",
        denied: "Cambia a otra cuenta.",
      },
      emptyBook: "Aún sin profundidad",
      emptyBookText: "El libro se llena a medida que el exchange envía sus niveles.",
      emptyTape: "Aún sin operaciones",
      emptyTapeText: "Las operaciones aparecen aquí en cuanto ocurren.",
      emptyChart: "Aún sin historial de precios",
      emptyChartText: "Las velas empiezan con las primeras operaciones.",
      opensAt: "Abre {time}",
      proxy: "Precio de referencia",
      proxyWhy: "Precio de un mercado de referencia (sin licencia de datos CME).",
      proxyRef: { index: "Referencia: el índice subyacente.", spot: "Referencia: el precio spot.", front_month: "Referencia: el precio del contrato más cercano." },
    },
    keys: {
      title: "Teclado y ratón",
      armed: "Teclas de trading activas",
      trading: "Teclas de trading",
      off: "Desactivadas: actívalas para operar desde el teclado.",
      anywhere: "En cualquier lugar",
      chart: "En el gráfico",
      armHint: "Teclas de trading: B comprar, S vender a mercado, X cancelar todo, F cerrar posiciones",
      gestures: {
        limit: "Shift+clic: una orden límite al precio",
        stop: "Alt+clic: una orden stop al precio",
        drag: "Arrastra la línea de una orden para moverla",
        bracket: "Arrastra desde la posición para un take profit o un stop loss",
        zoom: "Rueda: zoom",
        scale: "Arrastra la escala de precios para estirarla, doble clic para restablecer",
      },
    },
    noMatch: "No se encontró nada",
  },
  chart: {
    quick: "Operación rápida",
    quickHide: "Ocultar operación rápida",
    tvLoading: "Cargando el gráfico de TradingView",
    tf: "Temporalidad",
    layers: "Capas",
    heatmap: "Mapa de calor",
    profile: "Perfil de volumen",
    bubbles: "Operaciones grandes",
    vwap: "VWAP",
    ema: "EMA",
    bb: "Bollinger",
    volume: "Volumen",
    gamma: "Niveles gamma",
    gammaNone: "La gamma de opciones está disponible para BTC y ETH",
    gammaLoading: "Cargando opciones…",
    gammaInfo: "Opciones de Deribit · {n} vencimientos · {time}",
    callWall: "Call wall",
    putWall: "Put wall",
    flip: "Gamma flip",
    settings: "Ajustes",
    emaFast: "EMA rápida",
    emaSlow: "EMA lenta",
    bbPeriod: "Periodo de Bollinger",
    bbWidth: "Ancho de Bollinger, σ",
    reset: "Restablecer vista",
    more: "Más",
    buyMkt: "Comprar",
    sellMkt: "Vender",
    qty: "Cantidad",
    long: "LARGO",
    short: "CORTO",
    tp: "TP",
    sl: "SL",
    lmt: "LMT",
    stp: "STP",
    plus: "Orden a {price}",
    buyLimit: "Compra límite {qty} @ {price}",
    sellLimit: "Venta límite {qty} @ {price}",
    buyStop: "Compra stop {qty} @ {price}",
    sellStop: "Venta stop {qty} @ {price}",
    help: "Shift+clic: límite · Alt+clic: stop · arrastra una línea para moverla · arrastra desde la posición para TP/SL · rueda: zoom · arrastra la escala de precios para estirarla, doble clic para restablecer",
    heatNote: "El mapa de calor y las operaciones grandes se registran mientras el gráfico está abierto.",
    view: "Vista del gráfico",
    views: { desk: "Nuestro gráfico", deskShort: "Nuestro", tv: "TradingView" },
    tvNote: "Los precios aquí vienen de TradingView y pueden diferir de los precios a los que se ejecutan tus órdenes.",
    tvBy: "Gráfico de TradingView",
    tvHint: "Los intervalos, indicadores y herramientas de dibujo están en la barra de TradingView",
    older: "Cargando historial anterior…",
    olderBusy: "El historial está ocupado — reintentando en unos segundos",
    look: {
      title: "Apariencia",
      theme: "Tema",
      themes: { obsidian: "Funded Lime", midnight: "Midnight", graphite: "Graphite", classic: "Classic", aurora: "Aurora", mono: "Mono", paper: "Paper" },
      candles: "Velas",
      styles: { filled: "Rellenas", hollow: "Huecas", bars: "Barras", heikin: "Heikin-Ashi" },
      background: "Fondo",
      bgModes: { solid: "Sólido", gradient: "Degradado", glow: "Brillo", dots: "Puntos" },
      colors: { up: "Alza", down: "Baja", background: "Fondo" },
      grid: "Cuadrícula",
      watermark: "Marca de agua del símbolo",
      reset: "Restablecer apariencia",
    },
  },
  pro: {
    openInterest: "Interés abierto",
    funding: "Funding",
    fundingValue: "{rate} / {h} h",
    mark: "Mark",
    change24h: "Cambio 24h",
    high24h: "Máximo 24h",
    low24h: "Mínimo 24h",
    volume24h: "Volumen 24h",
    spread: "Spread",
    total: "Total",
    long: "Comprar / Largo",
    short: "Vender / Corto",
    available: "Disponible para operar",
    mid: "Medio",
    sizePct: "Tamaño, % del poder de compra",
    tpsl: "Take profit / stop loss",
    submitLong: "Comprar {qty} {base}",
    submitShort: "Vender {qty} {base}",
    orderValue: "Valor de la orden",
    entry: "Entrada est.",
    maxSize: "Tamaño máx.",
    riskAtStop: "Riesgo en el stop",
    account: "Cuenta",
  },
  ticket: {
    title: "Orden",
    costDetails: "Detalle de costos",
    buy: "Comprar",
    sell: "Vender",
    type: "Tipo",
    types: { market: "Mercado", limit: "Límite", stop: "Stop", stop_limit: "Stop límite" },
    qty: "Cantidad",
    limitPx: "Precio límite",
    stopPx: "Precio stop",
    tif: "Vigencia",
    reduceOnly: "Solo reducir",
    postOnly: "Solo maker",
    bracket: "Take profit / stop loss",
    tp: "Take profit",
    sl: "Stop loss",
    risk: "Riesgo si salta el stop",
    riskOfBalance: "{amount} · {pct} del balance",
    sizeFromRisk: "Tamaño por riesgo, %",
    apply: "Aplicar",
    noStop: "Añade un stop loss para ver el riesgo antes de enviar.",
    submit: "{side} {qty} {symbol}",
    submitted: "Enviada — esperando al motor",
    rejected: "Rechazada: {code}",
    filled: "Ejecutada {qty} @ {px}",
    realized: "P&L {v}",
    side: "Lado",
    max: "Máx.",
    lots: "lotes",
    maxNow: "máx. {qty} {base} a {lev}×",
    amountUsd: "Monto, USD",
    unit: "Unidad",
    position: "Posición",
    tpslIn: "TP / SL en",
    modes: { price: "Precio" },
    slRequired: "Este programa exige un stop loss en cada orden.",
    marginAt: "Margen a {lev}×",
    tradingFee: "Comisión de trading",
    leverageFee: "Comisión por apalancamiento",
    tradingFeeAt: "Comisión de trading {rate}",
    leverageFeeAt: "Comisión por apalancamiento {rate}",
    totalCost: "Total para abrir",
    freeMargin: "Margen libre",
    powerHint: "Margen libre × apalancamiento {lev}×",
    liqEst: "Liquidación est.",
    liqNote: "La liquidación es una estimación: el precio en el que la equity cae al margen de mantenimiento ({rate} del nocional), con las demás posiciones tal como están. El apalancamiento no la mueve.",
    why: {
      inactive: "La cuenta está {status}",
      closed: "El mercado está cerrado",
      noPrice: "Esperando un precio",
      noLimit: "Ingresa el precio límite",
      noStop: "Ingresa el precio stop",
      noSize: "Ingresa un tamaño",
      belowMin: "El tamaño mínimo es {min} {base}",
      overMax: "Supera el poder de compra a {lev}×: como máximo {qty} {base}",
      reduceMax: "Solo reducir: como máximo {qty} {base}",
      tpSide: "El take profit debe estar {dir} del precio de entrada",
      slSide: "El stop loss debe estar {dir} del precio de entrada",
      above: "por encima",
      below: "por debajo",
    },
    results: "Resultados de órdenes",
    sending: "Enviando…",
    working: "Activa — espera su precio",
    refused: "Rechazada: {why}",
    actionFailed: "No se hizo",
    reasons: {
      INSUFFICIENT_MARGIN: "no hay suficiente margen libre",
      REDUCE_ONLY: "solo reducir abriría una posición",
      NO_LIQUIDITY: "no hay liquidez en el libro",
      FOK_NOT_FILLED: "no se pudo ejecutar completa",
      POST_ONLY_WOULD_TAKE: "solo maker tomaría liquidez",
      IOC_REMAINDER: "el resto se canceló (inmediata o cancelar)",
      POSITION_LIMIT: "supera el límite de posición del programa",
      MAX_OPEN_POSITIONS: "demasiadas posiciones abiertas",
      STOP_LOSS_REQUIRED: "este programa exige un stop loss",
      INVALID_BRACKET: "take profit o stop loss del lado equivocado del precio",
      FLAT_BY_TIME: "las posiciones se cierran a la hora de cierre del programa — nuevas desde la próxima sesión",
      WEEKEND_CLOSED: "el programa cierra FX / CFD el fin de semana — nuevas posiciones desde el domingo 17:00 Nueva York",
      NEWS_WINDOW: "ventana de noticias de alto impacto — puedes cerrar, no abrir ni añadir",
      INSTRUMENT_NOT_ALLOWED: "este instrumento no está en el programa",
      ACCOUNT_HALTED: "el trading en esta cuenta se detuvo",
      ROLL_WINDOW: "el contrato está en rollover",
      OCO: "se ejecutó la otra pata del bracket",
      LEVERAGE_OUT_OF_RANGE: "apalancamiento fuera del rango permitido",
      LEVERAGE_INSUFFICIENT_MARGIN: "las posiciones abiertas necesitan más margen con ese apalancamiento",
    },
    leverage: {
      title: "Apalancamiento",
      unavailable: "El apalancamiento aún no se puede cambiar aquí",
      forInstrument: "para {symbol}",
      forAccount: "para toda la cuenta",
      less: "Menos",
      more: "Más",
      margin: "Margen para el tamaño de la orden",
      noSize: "indica un tamaño",
      maxAt: "Tamaño máx. a {v}×",
      apply: "Fijar {v}×",
      pending: "Fijando {v}×…",
      applied: "El apalancamiento es {v}×",
      range: "Un número entero de {min}× a {max}×",
      marginUsed: "Margen usado",
      liqSame: "El apalancamiento cambia el margen, no el precio de liquidación.",
      needsMargin: "A {v}× las posiciones abiertas necesitan {need} de margen; la equity es {equity}.",
      refused: "Sin cambios: {why}",
      noAnswer: "el motor no respondió, el apalancamiento sigue igual",
      cancel: "Cancelar",
    },
  },
  blotter: {
    positions: "Posiciones",
    orders: "Órdenes",
    fills: "Ejecuciones",
    trades: "Operaciones",
    instrument: "Instrumento",
    side: "Lado",
    qty: "Cant.",
    avgPx: "Precio prom.",
    markPx: "Precio",
    upnl: "P&L no realizado",
    rpnl: "P&L realizado",
    type: "Tipo",
    price: "Precio",
    status: "Estado",
    filled: "Ejecutada",
    fee: "Comisión",
    liquidity: "Liquidez",
    time: "Hora",
    entry: "Entrada",
    exit: "Salida",
    pnl: "P&L",
    cancel: "Cancelar",
    close: "Cerrar",
    flatten: "Cerrar todas",
    cancelAll: "Cancelar todas",
    empty: "Nada aún",
    why: "Por qué aquí",
    emptyTitle: { positions: "Sin posiciones abiertas", orders: "Sin órdenes activas", fills: "Aún sin ejecuciones", trades: "Aún sin operaciones cerradas" },
    emptyText: {
      positions: "Una posición que abras desde la orden, el gráfico o la escalera aparece aquí con su P&L en vivo.",
      orders: "Las órdenes límite y stop esperan aquí hasta que se ejecuten o las canceles.",
      fills: "Cada ejecución, con el mercado en ese momento (Por qué aquí).",
      trades: "Una operación completa aparece aquí cuando su posición se cierra.",
    },
    upnlHint: "Antes de comisiones, al precio con el que el motor valora la posición (el mismo del gráfico)",
    leverageFee: "Comisión por apalancamiento",
    marginAt: "Margen · {lev}×",
    leverageFeeHint: "Parte de la comisión: el cargo por operar con apalancamiento",
    snapshot: {
      title: "Mercado en la ejecución",
      model: "Modelo de ejecución",
      models: { walk: "recorrió el libro", queue: "se alcanzó la posición en la cola", trade_through: "el precio se operó a través", quote: "contra la cotización", tiers: "profundidad sintética" },
      latency: "Latencia",
      quoteAge: "Antigüedad de la cotización",
      policy: "Política de ejecución",
      book: "Libro (niveles superiores)",
    },
    long: "Largo",
    short: "Corto",
    buy: "Compra",
    sell: "Venta",
  },
  risk: {
    title: "Riesgo",
    dll: "Pérdida diaria restante",
    ml: "Pérdida máxima restante",
    target: "Objetivo",
    days: "Días de trading",
    floor: "piso {v}",
    limit: "límite {v}",
    near: "Cerca de un límite: {rule} — {left}.",
    paused: "El trading está en pausa hasta la próxima sesión (pérdida diaria).",
    breached: "Esta cuenta está detenida: se infringió una regla del programa. El trading está cerrado; consulta Incumplimientos.",
    passed: "Fase superada. La siguiente fase se abre automáticamente.",
    practice: "Cuenta de práctica — sin reglas de programa.",
    microShare: "Ganancia de microscalping",
    microHint: "límite {max} % · operaciones de menos de {sec} s",
    microHeld: "El objetivo espera: el {share} % de la ganancia viene de operaciones mantenidas menos de {sec} s (límite {max} %). Sigue operando con operaciones más largas — esto no es un incumplimiento.",
    microPayout: "Los payouts esperan: el {share} % de la ganancia desde el último payout viene de operaciones mantenidas menos de {sec} s (límite {max} %).",
    streak: "Días con ganancia seguidos",
    streakHint: "cada uno ≥ {bar} · necesario para un payout",
    status: "Cuenta",
  },
  accounts: {
    emptyLead: "Elige un challenge, opera el libro en vivo, cobra.",
    step1: "Elige un challenge",
    step1Note: "Tamaño, reglas y precio antes de pagar.",
    step2: "Opera el libro en vivo",
    step2Note: "Ejecuciones en 10–30 ms, cada una explicada.",
    step3: "Cobra hasta el 90 %",
    step3Note: "Payouts a pedido una vez que apruebes.",
    pnlTotal: "P&L desde el inicio",
    count: "Cuentas",
    active: "Activas",
    type: "Tipo",
    size: "Tamaño de cuenta",
    startFree: "Comprar el challenge",
    allPrograms: "Todos los programas y opciones",
    title: "Cuentas",
    lead: "Cada cuenta que operas, con su equity y su situación.",
    total: "Equity total de tus cuentas",
    sinceStart: "desde el inicio",
    emptyTitle: "Tu escritorio está vacío",
    newTitle: "Abrir una cuenta",
    empty: "Aún no hay cuentas. Compra un challenge para empezar.",
    practice: "Práctica",
    challenge: "Challenge",
    funded: "Funded",
    startChallenge: "Iniciar un challenge",
    balance: "Balance",
    equity: "Equity",
    open: "Abrir",
    trade: "Operar",
    program: "Programa",
    phase: "Fase",
    created: "Inicio",
    chooseMarket: "Mercado",
    chooseProgram: "Programa",
    start: "Iniciar",
  },
  dashboard: {
    monthNet: "Mes",
    tradingDays: "Días de trading",
    greenDays: "Días en verde",
    week: "Semana",
    fills: "{n} ejecuciones",
    daysN: "{n} días",
    overview: "Resumen",
    targetOf: "{progress} de {amount}",
    prevMonth: "Mes anterior",
    nextMonth: "Mes siguiente",
    equityCurve: "Equity",
    rules: "Reglas de esta versión",
    version: "Versión {n} · {hash}",
    trades: "Operaciones",
    calendar: "Calendario",
    breaches: "Incumplimientos",
    certificates: "Certificados",
    history: "Historial",
    stepper: "Fases",
    current: "actual",
    realized: "P&L realizado",
    fees: "Comisiones",
    openTerminal: "Abrir terminal",
    noTrades: "Aún sin operaciones cerradas",
    certificate: "Certificado",
    viewCertificate: "Abrir certificado",
    noCertificates: "Un certificado aparece cuando se supera una fase.",
    dayNet: "{v} neto · {n} ejecuciones",
    weekdays: "Lun,Mar,Mié,Jue,Vie,Sáb,Dom",
  },
  certificate: {
    heading: "Certificado de logro",
    body: "{name} superó {phase} de {program} (versión {version}).",
    balance: "Balance final {balance} en una cuenta de {start}.",
    days: "{n} días de trading.",
    issued: "Emitido {date} · ID {id}",
    demo: "Evaluación en una cuenta simulada. Este certificado no es una oferta de contrato de trader funded.",
    print: "Imprimir",
  },
  cme: {
    title: "Datos de mercado de CME — acuerdo de suscriptor",
    frame: "El texto final del acuerdo aprobado por CME se muestra aquí cuando la licencia de datos de mercado esté vigente.",
    intro: "Antes de mostrar datos de futuros, CME exige que cada usuario acepte el acuerdo de suscriptor e indique si es profesional.",
    q1: "¿Estás registrado ante un regulador de valores o de futuros?",
    q2: "¿Operas para alguien que no seas tú?",
    q3: "¿Usas los datos para un negocio?",
    no: "No",
    yes: "Sí",
    pro: "Los suscriptores profesionales aún no son compatibles.",
    name: "Nombre completo como firma",
    sign: "Firmar como no profesional",
    signed: "Firmado",
  },
  settings: {
    title: "Ajustes",
    lead: "Cómo se comporta la terminal para ti, y los acuerdos que exigen los datos de mercado.",
    trading: "Trading",
    data: "Datos de mercado",
    defaultQty: "Cantidad predeterminada",
    confirm: "Preguntar antes de enviar una orden",
    confirmNote: "Una confirmación más antes de que cada orden salga del ticket.",
    hotkeysOn: "Atajos de trading",
    hotkeysNote: "Atajos de teclado en la terminal: compra y venta a mercado, cancelar todo, cerrar posiciones, buscar.",
    saved: "Guardado",
    cmeNote: "Necesario antes de mostrar datos de futuros. Toma un minuto.",
    account: "Cuenta e inicio de sesión",
    mfaOff: "Desactivada — protege el inicio de sesión y los payouts con un segundo factor.",
    mfaSetUp: "Configurar",
    profile: "Perfil y contraseña",
    edit: "Editar",
    notifications: "Notificaciones por correo",
    accountEmails: "Correos sobre mis cuentas",
    accountEmailsNote: "Cuenta lista, fase superada, cuenta funded, incumplimiento. Los recibos, payouts, retiros y respuestas de soporte llegan siempre.",
  },
  site: {
    heroTitle: "Tu propia mesa prop en cripto, FX y futuros",
    heroBody: "Una terminal, un reglamento por programa, cada ejecución explicada. Trading simulado con datos de mercado reales.",
    cta: "Ver programas",
    signIn: "Iniciar sesión",
    markets: "Tres mercados, una plataforma",
    marketText: {
      crypto: "Perpetuos en un libro de órdenes en vivo, funding real.",
      fx: "Pares principales y oro con spreads y swaps reales.",
      futures: "Futuros CME de índices, petróleo y oro con DOM.",
    },
    honest: "Condiciones honestas",
    honestBody: "Las reglas de cada versión quedan fijadas al comprar. Sin cambios sorpresa, sin filtros ocultos: la pérdida diaria, la pérdida máxima y el límite del día están explicados.",
    programs: "Programas",
    compare: "Comparar",
    size: "Cuenta",
    price: "Precio",
    target: "Objetivo",
    dll: "Pérdida diaria",
    ml: "Pérdida máx.",
    days: "Días mín.",
    split: "Reparto",
    perMonth: "/ mes",
    launch: "precio de lanzamiento",
    configurator: "Configurar",
    family: "Programa",
    addons: "Extras",
    cmeL2: "Profundidad CME (L2)",
    total: "Total",
    rules: "Reglas completas",
    getStarted: "Empezar",
    dayEnds: "El día de trading termina {at}",
    rulesHash: "Hash de reglas {hash}",
    programsLead: "Elige un mercado, un programa y un tamaño. El precio y las reglas de la versión están en la página antes de comprar.",
    faqLead: "Respuestas cortas sobre cuentas, ejecuciones y reglas.",
    consistency: "Consistencia",
    microScalp: "Microscalping",
    microScalpNote: "operaciones mantenidas menos de {sec} s",
    microScalpCell: "≤ {v} % de la ganancia",
    streak: "Antes de un payout",
    streakCell: "{n} días seguidos ≥ {v}",
    daysCell: "{n} días ≥ {v}",
    bestDayCell: "mejor día ≤ {v} % de la ganancia",
    minProfitCell: "ganancia ≥ {v}",
    cycle: { weekly: "cada 7 días", bi_weekly: "cada 14 días", monthly: "cada 30 días" },
    fundedOnly: "cuenta funded",
    fairPlay: "Juego limpio",
    fairPlayCell: "Están prohibidas las posiciones opuestas o de cobertura entre cuentas — tuyas, de otras personas o de cuentas en otras firmas —, así como el copy trading y el trading en grupo. También varios perfiles de una misma persona y el arbitraje de latencia o de errores de precio: los perfiles vinculados no reciben payouts. Cualquier abuso, esquema o práctica fraudulenta nos permite rechazar un payout y cerrar las cuentas implicadas (consulta Prácticas prohibidas).",
    scaling: "Escalado",
    scalingCell: "+{step} % cada {months} meses, hasta {max}",
    segmentCell: "la ganancia por encima del tope de payout se queda en la cuenta",
    carryCell: "la ganancia por encima del tope de payout se puede retirar más tarde",
    resetCell: "balance de vuelta al inicial tras el payout #{n}, luego sin topes",
    fundingLimit: "Límite de financiación",
    fundingLimitCell: "{v} por trader en este programa",
    daysBar: "un día cuenta con ≥ {v} % de ganancia",
    tradeLoss: "Pérdida máx. por operación",
    tradeLossNote: "una operación abierta infringe la regla en cuanto su pérdida flotante la supera",
    time: "Límite de tiempo",
    windowCell: "{h} h desde la primera operación",
    daysN: "{n} días",
    maxDays: "Días de trading máx.",
    inactivity: "Inactividad",
    inactivityCell: "{n} días sin operar cierran la cuenta",
    contracts: "Contratos máx.",
    contractsCell: "{n} por instrumento",
    flat: "Posiciones cerradas a las",
    flatNote: "hora de {city}; nada nuevo hasta la próxima sesión",
    weekend: "Fin de semana",
    weekendCell: "posiciones cerradas vie 16:55 Nueva York",
    weekendNote: "nada nuevo hasta dom 17:00 Nueva York",
    news: "Noticias de alto impacto",
    newsCell: "sin operaciones nuevas ±{n} min",
    newsNote: "cerrar siempre está permitido",
    faq: "Preguntas frecuentes",
  },
} satisfies Messages<typeof en>;
